import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Libellés multilingues venant de l'API.
 *
 * L'API expose les trois traductions plutôt qu'un libellé déjà résolu : le
 * client change de langue sans rappeler le serveur, et rien n'oblige à
 * transporter la langue dans chaque requête. La contrepartie est qu'un seul
 * endroit doit décider laquelle afficher — sinon deux écrans divergent.
 */
export interface LocalizedNames {
  /** Libellé historique, servant de repli. */
  name?: string | null;
  nameAr?: string | null;
  nameFr?: string | null;
  nameEn?: string | null;
}

@Injectable({ providedIn: 'root' })
export class LocalizedLabelService {
  private readonly translate = inject(TranslateService);

  /**
   * Libellé dans la langue active.
   *
   * Repli en cascade : la traduction demandée, puis l'arabe (langue par défaut
   * du produit), puis le libellé historique. Une traduction manquante affiche
   * donc un nom lisible plutôt qu'un blanc.
   */
  pick(source: LocalizedNames | null | undefined): string {
    if (!source) return '';

    const byLang: Record<string, string | null | undefined> = {
      ar: source.nameAr,
      fr: source.nameFr,
      en: source.nameEn,
    };

    const lang = this.translate.currentLang || 'ar';
    return (
      firstNonEmpty(byLang[lang], source.nameAr, source.name) ?? ''
    );
  }

  /**
   * Variante pour les objets qui préfixent leurs champs — `subjectName`,
   * `subjectNameFr`… plutôt que `name`, `nameFr`.
   */
  pickPrefixed(
    source: Record<string, unknown> | null | undefined,
    prefix: string,
  ): string {
    if (!source) return '';

    const cap = (suffix: string) =>
      source[`${prefix}${suffix}`] as string | null | undefined;

    return this.pick({
      name: cap(''),
      nameAr: cap('Ar'),
      nameFr: cap('Fr'),
      nameEn: cap('En'),
    });
  }
}

function firstNonEmpty(
  ...values: (string | null | undefined)[]
): string | undefined {
  for (const v of values) {
    if (v && v.trim()) return v;
  }
  return undefined;
}
